import React, { useContext, useState } from 'react';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, CreditCard, Loader2, LogOut } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SubscriptionPaywall() {
  const { companyContext, token, user, logout } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);

  if (!companyContext?.subscription_expired) return null;
  // Super admins, demo users bypass paywall
  if (user?.role === 'super_admin' || user?.role === 'demo' || user?.is_demo) return null;

  const info = companyContext.subscription_overdue_info || {};
  const company = companyContext.company || {};
  const planId = info.plan || 'basic';
  const billingCycle = info.billing_cycle || 'monthly';

  const handleRenew = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/subscriptions/renew`, {
        plan_id: planId,
        company_id: companyContext.company_id,
        origin_url: window.location.origin,
        billing_cycle: billingCycle
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      toast.error('Unable to process payment right now. Please contact info@memorialdesk.co.uk for assistance.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/95 backdrop-blur-sm"
      data-testid="subscription-paywall"
    >
      <Card className="w-full max-w-lg mx-4 bg-white border-0 shadow-2xl">
        <CardContent className="p-8 text-center space-y-6">
          <div className="mx-auto w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
            <AlertTriangle className="h-8 w-8 text-red-600" />
          </div>

          <div className="space-y-2">
            <h1 className="text-2xl font-bold text-slate-900">
              Subscription Overdue
            </h1>
            <p className="text-slate-600">
              Your subscription for <strong>{company.name || 'your company'}</strong> expired
              {info.days_overdue > 0 ? ` ${info.days_overdue} day${info.days_overdue === 1 ? '' : 's'} ago` : ''}.
              Please renew to continue using the platform.
            </p>
          </div>

          <div className="bg-slate-50 rounded-lg p-4 text-left space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Plan</span>
              <span className="font-medium text-slate-900 capitalize">{planId}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Billing</span>
              <span className="font-medium text-slate-900 capitalize">{billingCycle}</span>
            </div>
            {info.monthly_price > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Amount</span>
                <span className="font-bold text-slate-900">
                  {billingCycle === 'yearly'
                    ? `£${(info.monthly_price * 12).toFixed(2)}/year`
                    : `£${info.monthly_price.toFixed(2)}/month`}
                </span>
              </div>
            )}
          </div>

          <div className="space-y-3">
            <Button
              onClick={handleRenew}
              disabled={loading}
              className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white text-base font-semibold"
              data-testid="renew-subscription-btn"
            >
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
              ) : (
                <CreditCard className="h-5 w-5 mr-2" />
              )}
              {loading ? 'Redirecting to payment...' : 'Renew Now'}
            </Button>

            <Button
              variant="ghost"
              onClick={logout}
              className="w-full text-slate-500 hover:text-slate-700"
              data-testid="paywall-logout-btn"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </Button>
          </div>

          <p className="text-xs text-slate-400">
            Having trouble? Contact support at info@memorialdesk.co.uk
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
